import type { PayloadAction } from '@reduxjs/toolkit';
import { createSelector, createSlice } from '@reduxjs/toolkit';

import { fetchArchivedMedications } from 'src/app/client/models/account';
import { getCurrentAccountId } from 'src/app/state/selectors';
import type { Dispatch, RootState } from 'src/app/state/store';
import type { HomeArchivedItem } from 'src/app/types';

type InitialStateArchivedSlice = {
  isFetching: boolean;
  error: string | null;
  archived: HomeArchivedItem[] | null;
};

const initialState: InitialStateArchivedSlice = {
  isFetching: false,
  error: null,
  archived: null,
};

const archivedSlice = createSlice({
  name: 'archivedMedications',
  initialState,
  reducers: {
    fetchArchivedRequest(state) {
      state.isFetching = true;
    },
    fetchArchivedSuccess(state, action: PayloadAction<HomeArchivedItem[]>) {
      state.isFetching = false;
      state.archived = action.payload;
    },
    fetchArchivedFailure(state, action: PayloadAction<string>) {
      state.isFetching = false;
      state.error = action.payload;
    },
  },
});

/**
 * Thunks
 *
 * @description For when you need to do async stuff or when you need to
 * access state from other slices/reducers.
 */

export function loadArchivedMedications(newAccountId?: string) {
  return async (dispatch: Dispatch, getState: () => RootState) => {
    const state = getState();
    const { client } = state;
    dispatch(archivedSlice.actions.fetchArchivedRequest());
    try {
      const accountId = newAccountId ?? getCurrentAccountId(state);
      const response = await fetchArchivedMedications(client, { accountId });
      const archivedMedications = response.data;
      dispatch(archivedSlice.actions.fetchArchivedSuccess(archivedMedications));
    } catch (err) {
      dispatch(archivedSlice.actions.fetchArchivedFailure(err.message));
    }
  };
}

/** Selectors */
const getArchived = (state: RootState) => state?.archivedMedications;
export const getArchivedMedications = createSelector(getArchived, state => state?.archived);
const getPrescriptionIdInPath = (_state: RootState, ownProps) => ownProps.match.params.prescriptionId;
export const getIsMedArchived = createSelector(
  [getArchived, getPrescriptionIdInPath],
  (archivedState, prescriptionId) => archivedState?.archived?.find(archivedItem => archivedItem.id === prescriptionId)
);
export const getIsFetchingArchivedMedications = createSelector(getArchived, state => state?.isFetching);

export default archivedSlice.reducer;
